import React, { useEffect, useState } from "react";
import "./RightSidebar.css";
import { colorCodes } from "../utils/color.codes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { Template } from "./interfaces/template.interface";
import { TripType } from "@industronics/fids-utils";

interface RightSidebarProps {
  selectedTextAlignment: string,
  selectedTemplate: Template,
  selectedTripType: string,
  selectedDateFormat: string;
  cellLabel: string | null;
  isHeader: boolean;
  selectedLabel: string,
  selectedField: string | null;
  selectedFontSize: string;
  selectedFontColor: string;
  selectedBgColor: string;
  selectedPageBgColor: string;
  alternateBgColor: string;
  alternateFontColor: string;
  selectedColumnWidth: string;
  selectedFontWeight: string;
  selectedFontFamily: string;
  onFontWeightChange: (weight: string) => void;
  onFontFamilyChange: (family: string) => void;
  onColumnWidthChange: (width: string) => void;
  onTripTypeChange: (tripType: string) => void,
  onDateFormatChange: (format: string) => void,
  onFontColorChange: (color: string) => void;
  onBgColorChange: (color: string) => void;
  onLabelChange: (label: string) => void;
  onPageBgColorChange: (color: string) => void;
  onFontSizeChange: (size: string) => void;
  onTextAlign: (alignment: string) => void;

  schemaFields: { field: string; label: string }[];
  templateColumns: string[]; // Fields currently in the template columns
  onColumnsChange: (updatedColumns: string[]) => void; // Callback to update the columns
  showFlightsInlineToggle: () => void
  animationDirection: string;
  onAnimationDirectionChange: (direction: string) => void;
  flipSpeed: string;
  onFlipSpeedChange: (speed: string) => void;
}


const RightSidebar: React.FC<RightSidebarProps> = ({
  selectedFontWeight,
  selectedFontFamily,
  onFontWeightChange,
  onFontFamilyChange,
  selectedColumnWidth,
  onColumnWidthChange,
  selectedTextAlignment,
  selectedFontSize,
  selectedField,
  selectedTemplate,
  selectedTripType,
  selectedDateFormat,
  onFontSizeChange,
  onTripTypeChange,
  onDateFormatChange, cellLabel, isHeader, alternateFontColor,
  alternateBgColor, selectedPageBgColor, selectedLabel,
  onPageBgColorChange, onLabelChange,
  selectedBgColor, selectedFontColor, onFontColorChange, onBgColorChange,
  schemaFields,
  templateColumns,
  onTextAlign,
  onColumnsChange,
  showFlightsInlineToggle,
  animationDirection,
  onAnimationDirectionChange,
  flipSpeed,
  onFlipSpeedChange,
}) => {


  const showFlightsInline = (): boolean => {
    const column = selectedTemplate?.draft?.columns.find(x => x.field === "flightNumber");
    return column?.body?.showFlightsInline || false;
  }

  const uniqueColors = Array.from(
    new Map(colorCodes.map((color) => [color.value, color])).values()
  );

  const dateFormats = [
    { label: "DD MMM (e.g., 05 Sep)", value: "DD MMM" },
    { label: "MMMM DD (e.g., September 05)", value: "MMMM DD" },
    { label: "DD-MM (e.g., 05-09)", value: "DD-MM" },
    { label: "MM/DD (e.g., 09/05)", value: "MM/DD" },
    { label: "Do MMMM (e.g., 5th September)", value: "Do MMMM" },
    { label: "DD/MM (e.g., 05/09)", value: "DD/MM" },
  ];

  const tripTypes = [TripType.Arrival, TripType.Departure];

  const flightTypes = ["All", "Passenger", "Cargo"];

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const handleCheckboxChange = (field: string, checked: boolean) => {
    let updatedColumns;

    if (checked) {
      // Add field to template columns
      updatedColumns = [...templateColumns, field];
    } else {
      // Remove field from template columns
      updatedColumns = templateColumns.filter((col) => col !== field);
    }

    onColumnsChange(updatedColumns); // Notify parent
  };

  const textAlignments = [
    { label: "Left", value: "start" },
    { label: "Right", value: "end" },
    { label: "Center", value: "center" }
  ];

  const [isCustomMode, setIsCustomMode] = useState(false);

  // Sync on mount/when selectedFontSize changes from outside
  useEffect(() => {
    const isPreset = fontSizes.some(
      s => s.value !== 'custom' && s.value === selectedFontSize
    );
    if (!isPreset) setIsCustomMode(true);
  }, [selectedFontSize]);

  const fontSizes: any[] = [
    { text: "Small", value: "20px" },
    { text: "Medium", value: "23px" },
    { text: "Large", value: "25px" },
    { text: "Larger", value: "27px" },
    { text: "X-large", value: "30px" },
    { text: "XX-large", value: "40px" },
    { text: "Custom", value: "custom" },
  ];

  const fontWeights = [
    { text: 'Thin', value: '100' },
    { text: 'Light', value: '300' },
    { text: 'Normal', value: '400' },
    { text: 'Medium', value: '500' },
    { text: 'Semi Bold', value: '600' },
    { text: 'Bold', value: '700' },
    { text: 'Extra Bold', value: '800' },
  ];

  const fontFamilies = [
    { text: 'Default (Sans)', value: 'sans-serif' },
    { text: 'Arial', value: 'Arial' },
    { text: 'Helvetica', value: 'Helvetica' },
    { text: 'Verdana', value: 'Verdana' },
    { text: 'Tahoma', value: 'Tahoma' },
    { text: 'Trebuchet MS', value: 'Trebuchet MS' },
    { text: 'Georgia', value: 'Georgia' },
    { text: 'Times New Roman', value: 'Times New Roman' },
    { text: 'Courier New', value: 'Courier New' },
    { text: 'Lucida Console', value: 'Lucida Console' },
  ];

  return (
    <aside className="right-sidebar">
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button className="nav-link active">Props</button>
        </li>
      </ul>
      <div className="tab-content">
        <div className="tab-pane active">

          <div className="dropdown mt-2">
            <h6>Fields</h6>
            <button
              className="btn mng-fld-btn"
              onClick={toggleDropdown}
            >
              Manage Fields
              <FontAwesomeIcon icon={faAngleDown} className="ml-2 pull-right" />
            </button>
            {dropdownOpen && (
              <ul className="dropdown-menu">
                {schemaFields.map((field) => (
                  <li key={field.field} className="dropdown-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={templateColumns.includes(field.field)}
                        onChange={(e) =>
                          handleCheckboxChange(field.field, e.target.checked)
                        }
                      />
                      {field.label}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {isHeader && selectedField && (
            <div className="row mt-2">
              <div className="col-md-12">
                <h6>Column Width</h6>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    min={1}
                    max={100}
                    value={parseInt(selectedColumnWidth) || ''}
                    onChange={(e) => onColumnWidthChange(e.target.value + '%')}
                    placeholder="e.g. 20"
                  />
                  <span className="input-group-text">%</span>
                </div>
              </div>
            </div>
          )}

          {isHeader && (<div className="row">
            <div className="col-md-12">
              <h6>Column</h6>
              <input className="form-control" type="text"
                onChange={(e) => {
                  const newValue = e.target.value;
                  onLabelChange(newValue);
                }}
                value={selectedLabel || ""}
              />
            </div>
          </div>)}
          {(selectedField === "flightNumber") && (
            <div>
              {/* Only show inline toggle for flightNumber, not airline */}
              {selectedField === "flightNumber" && (
                <div className="form-check form-switch mt-2">
                  <input
                    onChange={showFlightsInlineToggle}
                    checked={showFlightsInline()}
                    className="form-check-input"
                    type="checkbox"
                    id="flexSwitchCheckInline"
                  />
                  <label className="form-check-label">Show flights inline</label>
                </div>
              )}
            </div>
          )}

          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Trip Type</h6>
              <select
                className="form-select"
                value={selectedTripType}
                onChange={(e) => onTripTypeChange(e.target.value)}
              >
                {tripTypes.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>


          {(cellLabel && selectedTemplate?.draft?.columns?.some(col => col.field === cellLabel && col.type === "date")) && (<div className="row mt-2">
            <div className="col-md-12">
              <h6>Date Format</h6>
              <select
                className="form-select"
                value={selectedDateFormat}
                onChange={(e) => onDateFormatChange(e.target.value)}
              >
                {dateFormats.map((format) => (
                  <option key={format.value} value={format.value}>
                    {format.label}
                  </option>
                ))}
              </select>
            </div>
          </div>)}

          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Animation Direction</h6>
              <select
                className="form-select"
                value={animationDirection}
                onChange={(e) => onAnimationDirectionChange(e.target.value)}
              >
                <option value="up">Scroll Up</option>
                <option value="down">Scroll Down</option>
              </select>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Animation Speed</h6>
              <select
                className="form-select"
                value={flipSpeed}
                onChange={(e) => onFlipSpeedChange(e.target.value)}
              >
                <option value="slow">Slow</option>
                <option value="normal">Normal</option>
                <option value="fast">Fast</option>
              </select>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Align Text</h6>
              <select
                className="form-select"
                value={selectedTextAlignment}
                onChange={(e) => onTextAlign(e.target.value)}
              >
                {textAlignments.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Font Weight</h6>
              <select
                className="form-select"
                value={selectedFontWeight}
                onChange={(e) => onFontWeightChange(e.target.value)}
              >
                {fontWeights.map((w) => (
                  <option
                    key={w.value}
                    value={w.value}
                    style={{ fontWeight: w.value }}
                  >
                    {w.text}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Font Family</h6>
              <select
                className="form-select"
                value={selectedFontFamily}
                onChange={(e) => onFontFamilyChange(e.target.value)}
                style={{ fontFamily: selectedFontFamily }}
              >
                {fontFamilies.map((f) => (
                  <option
                    key={f.value}
                    value={f.value}
                    style={{ fontFamily: f.value }}
                  >
                    {f.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Font Size</h6>
              <select
                className="form-select"
                value={isCustomMode ? 'custom' : selectedFontSize}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setIsCustomMode(true);
                    // Keep current font size value, just reveal the input
                  } else {
                    setIsCustomMode(false);
                    onFontSizeChange(e.target.value);
                  }
                }}
              >
                {fontSizes.map((size) => (
                  <option key={size.text} value={size.value}>
                    {size.text}{size.value !== 'custom' ? ` (${size.value})` : ''}
                  </option>
                ))}
              </select>

              {isCustomMode && (
                <div className="input-group mt-1">
                  <input
                    type="number"
                    className="form-control"
                    min={10}
                    max={80}
                    value={parseInt(selectedFontSize) || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) onFontSizeChange(val + 'px');
                    }}
                    placeholder="Enter size"
                    autoFocus
                  />
                  <span className="input-group-text">px</span>
                </div>
              )}
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Font Color</h6>
              <select
                className="form-select"
                value={selectedFontColor.toLowerCase()}
                onChange={(e) => onFontColorChange(e.target.value)}
              >
                {uniqueColors.map((color) => (
                  <option
                    style={{
                      backgroundColor: color.value,
                      color: color.value === "#000000" ? "#ffffff" : "#000000", // Ensure text is readable
                    }}
                    key={"color" + color.value} value={(color.value).toLowerCase()}>
                    {color.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Background Color</h6>
              <select
                className="form-select"
                value={selectedBgColor}
                onChange={(e) => onBgColorChange(e.target.value)}
              >
                {uniqueColors.map((color) => (
                  <option
                    style={{
                      backgroundColor: color.value,
                      color: color.value === "#000000" ? "#ffffff" : "#000000", // Ensure text is readable
                    }}
                    key={"bg" + color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <hr />
          <div className="row mt-2">
            <div className="col-md-12">
              <h6>Page Background Color</h6>
              <select
                className="form-select"
                value={selectedPageBgColor}
                onChange={(e) => onPageBgColorChange(e.target.value)}
              >
                {uniqueColors.map((color) => (
                  <option
                    style={{
                      backgroundColor: color.value,
                      color: color.value === "#000000" ? "#ffffff" : "#000000", // Ensure text is readable
                    }}
                    key={"page-bg" + color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default RightSidebar;
